import type { MatrixAsset } from "./report-matrix";

/** A plotted rail dot — creatives without a score never reach the track. */
export type ScoredAsset = MatrixAsset & { score: number };

/** One rail position: every creative sharing the same rounded score. */
export interface ScoreCluster {
  score: number;
  assets: ScoredAsset[];
}

/** Collapse creatives that share the same rounded score into one rail position. */
export function clusterAssetsByScore(
  assets: readonly ScoredAsset[],
): ScoreCluster[] {
  const byScore = new Map<number, ScoredAsset[]>();
  for (const asset of assets) {
    const key = Math.round(asset.score);
    const group = byScore.get(key) ?? [];
    group.push(asset);
    byScore.set(key, group);
  }
  return [...byScore.entries()]
    .sort(([a], [b]) => a - b)
    .map(([score, group]) => ({ score, assets: group }));
}

/** Mean score across the plotted creatives — the baseline a dot is compared against. */
export function averageScore(assets: readonly ScoredAsset[]): number {
  if (assets.length === 0) return 0;
  const total = assets.reduce((sum, asset) => sum + asset.score, 0);
  return total / assets.length;
}

/** 1-based rank of a score among the plotted creatives; ties share the better rank. */
export function rankForScore(
  assets: readonly ScoredAsset[],
  score: number,
): number {
  const better = assets.filter((asset) => Math.round(asset.score) > score);
  return better.length + 1;
}
